import './bulk-messages-list-item.scss';

import { kebabCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  BaseBulkItemDataFragment,
  BulkCampaignType,
} from '../../../../data/types/api/graphql';
import { makeKeyDownHandler } from '../../../../data/utils/make-key-down-handler';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';

type BulkMessagesListItemProps = {
  item: BaseBulkItemDataFragment;
  loading?: boolean;
  onClick?: (value: BaseBulkItemDataFragment) => void;
  onAddSlots?: (value: BaseBulkItemDataFragment) => void;
  onAddPatients?: (value: BaseBulkItemDataFragment) => void;
  onSendAll?: (value: BaseBulkItemDataFragment) => void;
};

function BulkMessagesListItem({
  item,
  loading,
  onClick,
  onAddPatients,
  onAddSlots,
  onSendAll,
}: BulkMessagesListItemProps) {
  const intl = useIntl();
  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(item),
        onKeyDown: makeKeyDownHandler(() => onClick(item)),
      };
    }

    return {};
  }, [item, onClick]);

  const onHandleAddPatients = useCallback(() => {
    if (onAddPatients) {
      onAddPatients(item);
    }
  }, [item, onAddPatients]);

  const onHandleAddSlots = useCallback(() => {
    if (onAddSlots) {
      onAddSlots(item);
    }
  }, [item, onAddSlots]);

  const { confirmed, declined, pending } = useMemo(
    () => ({
      confirmed: (item.confirmedPatientCount / item.invitedPatientCount) * 100,
      declined: (item.declinedPatientCount / item.invitedPatientCount) * 100,
      pending: (item.pendingResponseCount / item.invitedPatientCount) * 100,
    }),
    [
      item.confirmedPatientCount,
      item.declinedPatientCount,
      item.invitedPatientCount,
      item.pendingResponseCount,
    ]
  );

  const { date } = useMemo(
    () => ({
      date:
        item.availableSlotsCount > 0
          ? `(${intl.formatDate(item.firstAppointmentDate, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })} - ${intl.formatDate(item.lastAppointmentDate, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })})`
          : null,
    }),
    [
      intl,
      item.availableSlotsCount,
      item.firstAppointmentDate,
      item.lastAppointmentDate,
    ]
  );

  const all = item.invitedPatientCount + item.notSendCount;

  const sendDisabled = useMemo(() => {
    if (!item.active) return true;
    if (item.notSendCount === 0) return true;

    if ([BulkCampaignType.BulkInvite].includes(item.type)) {
      if (item.availableSlotsCount === 0) {
        return true;
      }
      return false;
    }
    return false;
  }, [item.active, item.availableSlotsCount, item.notSendCount, item.type]);

  return (
    <div {...attributes} className="bulk-messages-list-item">
      <div className="bulk-messages-list-item__top">
        <h3>
          {item.internalTitle}
          {date && <span>{date}</span>}
        </h3>
        <h4>
          <CustomFormattedMessage
            id={`common.bulk-type.${kebabCase(item.type)}`}
          />
        </h4>
      </div>
      {![BulkCampaignType.PlanAppointment, BulkCampaignType.Text].includes(
        item.type
      ) && (
        <div className="bulk-messages-list-item__bottom">
          <div className="bulk-messages-list-item-progress">
            {confirmed > 0 && (
              <div
                style={{ width: `${confirmed}%` }}
                className="bulk-messages-list-item-progress__item bulk-messages-list-item-progress__item--confirmed"
              />
            )}
            {declined > 0 && (
              <div
                style={{ width: `${declined}%` }}
                className="bulk-messages-list-item-progress__item bulk-messages-list-item-progress__item--declined"
              />
            )}
            {pending > 0 && (
              <div
                style={{ width: `${pending}%` }}
                className="bulk-messages-list-item-progress__item bulk-messages-list-item-progress__item--pending"
              />
            )}
          </div>
          <div className="bulk-messages-list-item-percentage">
            <div className="bulk-messages-list-item-percentage__content bulk-messages-list-item-percentage__content--invited">
              <h3>{item.invitedPatientCount.toString()}</h3>
              <p>
                <CustomFormattedMessage id="bulk-messages.invited" />
              </p>
            </div>
            <div className="bulk-messages-list-item-percentage__content bulk-messages-list-item-percentage__content--confirmed">
              <h3>{item.confirmedPatientCount.toString()}</h3>
              <p>
                <CustomFormattedMessage id="bulk-messages.confirmed" />
              </p>
            </div>
            <div className="bulk-messages-list-item-percentage__content bulk-messages-list-item-percentage__content--declined">
              <h3>{item.declinedPatientCount.toString()}</h3>
              <p>
                <CustomFormattedMessage id="bulk-messages.declined" />
              </p>
            </div>
            <div className="bulk-messages-list-item-percentage__content bulk-messages-list-item-percentage__content--pending">
              <h3>{item.pendingResponseCount.toString()}</h3>
              <p>
                <CustomFormattedMessage id="bulk-messages.pending" />
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="bulk-messages-list-item__buttons">
        <div className="bulk-messages-list-item__buttons__content">
          {![BulkCampaignType.PlanAppointment, BulkCampaignType.Text].includes(
            item.type
          ) && (
            <>
              <h3>{item.availableProceduresCount.toString()}</h3>
              <p>
                <CustomFormattedMessage id="bulk-messages.available-procedures" />
              </p>
            </>
          )}
        </div>
        {![BulkCampaignType.PlanAppointment, BulkCampaignType.Text].includes(
          item.type
        ) && (
          <div className="bulk-messages-list-item__buttons__content">
            <Button inverted onClick={onHandleAddSlots}>
              <span>
                {item.availableSlotsCount > 0
                  ? `${item.availableSlotsCount} `
                  : ''}
              </span>
              <CustomFormattedMessage id="bulk-messages.slots.title" />
            </Button>
          </div>
        )}
        <div className="bulk-messages-list-item__buttons__content">
          <Button inverted onClick={onHandleAddPatients}>
            <span>{all > 0 ? `${item.invitedPatientCount}/${all} ` : ''}</span>
            <CustomFormattedMessage id="bulk-messages.patients.title" />
          </Button>
        </div>
        <div className="bulk-messages-list-item__buttons__content">
          <Button
            type={ButtonType.ICON_ONLY}
            disabled={sendDisabled}
            icon={loading ? ButtonIcon.LOADING : ButtonIcon.PAPERPLANE}
            badge={item.notSendCount}
            onClick={() => {
              onSendAll?.(item);
            }}
          />
        </div>
      </div>
    </div>
  );
}
export { BulkMessagesListItem };
